import React from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import { EmptyState } from "@libertify/ui";
import { TrendUpIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { PresetDateRangePicker } from "components/PresetDateRangePicker/";
import { ANALYTICS_MAX_DATE_RANGE_MONTHS } from "constants/";
import type { UseAnalyticsPageResult } from "../../projects/hooks/useAnalyticsPage";
import { AnalyticsPageLayout } from "./AnalyticsPageLayout";
import { AnalyticsVersionSelect } from "./AnalyticsVersionSelect";

export type AnalyticsSubPageLayoutProps = {
  authKey: string;
  translationPrefix: string;
  page: UseAnalyticsPageResult<unknown>;
  /** Shows the empty state with this title even when the summary has data. */
  emptyTitle?: string;
  /** Second-level navigation shown first in the tab, e.g. the add-on pills. */
  subNav?: React.ReactNode;
  /** Shown on the right of the title, e.g. a link to a related page. */
  titleExtra?: React.ReactNode;
  /** The children don't read the summary: show them even without it. */
  childrenSkipSummary?: boolean;
  children: React.ReactNode;
};

export const AnalyticsSubPageLayout: React.FC<AnalyticsSubPageLayoutProps> = ({
  authKey,
  translationPrefix,
  page,
  emptyTitle,
  subNav,
  titleExtra,
  childrenSkipSummary = false,
  children,
}) => {
  const t = useTranslate();
  const noData =
    (page.isSuccess || page.isError) &&
    !page.summaryData &&
    !page.isDataLoading;
  const showEmpty = !childrenSkipSummary && (!!emptyTitle || noData);
  const showChildren =
    childrenSkipSummary ||
    (!emptyTitle && (!!page.summaryData || page.isDataLoading));

  return (
    <AnalyticsPageLayout
      authKey={authKey}
      projectId={page.projectId}
      projectTitle={page.project?.title}
      onBack={page.handleBack}
      dateRange={page.dateRange}
      version={page.version?.requested}
    >
      {subNav}
      {page.dateFiltered && (
        <PresetDateRangePicker
          value={page.dateRange}
          onChange={page.setDateRange}
          dateFormat="L"
          minDate={dayjs().subtract(ANALYTICS_MAX_DATE_RANGE_MONTHS, "months")}
          disabled={page.isDataLoading}
        />
      )}

      <Flex wrap justify="space-between" align="center" gap={8}>
        <Typography.Title level={3} style={{ margin: 0, fontWeight: "normal" }}>
          {t(`${translationPrefix}.title`)}
        </Typography.Title>
        {titleExtra}
        {page.version?.hasChoice ? (
          <div style={{ width: 320, maxWidth: "100%" }}>
            <AnalyticsVersionSelect
              versions={page.version.versions}
              value={page.version.selected}
              onChange={page.version.setVersion}
              loading={page.version.isLoading}
            />
          </div>
        ) : null}
      </Flex>

      {showEmpty ? (
        // Same empty state as the Overview tab.
        <Flex justify="center" align="center" style={{ marginTop: 100 }}>
          <EmptyState
            icon={<TrendUpIcon size={80} />}
            title={emptyTitle ?? t("media.projects.analytics.dashboard.noData")}
            // The hint talks about the selected period: only for date-filtered pages.
            description={
              !emptyTitle && page.dateFiltered
                ? t("media.projects.analytics.dashboard.noDataHint")
                : undefined
            }
          />
        </Flex>
      ) : null}

      {showChildren ? children : null}
    </AnalyticsPageLayout>
  );
};
